import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Handshake, Flag, ShieldCheck, MessageSquare, FileText, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import EvidenceThumb from "@/components/EvidenceThumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function ComplaintDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [messages, setMessages] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [text, setText] = useState("");
  const [resp, setResp] = useState({ response_type: "disagree", message: "" });
  const [sform, setSform] = useState({ proposed_action: "", refund_amount: "", completion_date: "", notes: "" });
  const chatEnd = useRef(null);

  const load = async () => {
    try {
      const cr = await api.get(`/complaints/${id}`);
      setC(cr.data);
      const [m, s] = await Promise.all([api.get(`/complaints/${id}/messages`), api.get(`/complaints/${id}/settlements`)]);
      setMessages(m.data); setSettlements(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load complaint");
    }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/complaints/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
  }, [id]);
  useEffect(() => {
    const h = (e) => { if (e.detail?.complaintId === id) api.get(`/complaints/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {}); };
    window.addEventListener("ca:new-message", h);
    return () => window.removeEventListener("ca:new-message", h);
  }, [id]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const isComplainant = c && user && c.complainant_id === user.id;
  const isAccused = c && user && (c.accused_id === user.id || c.party?.phone === user.phone);

  const sendMsg = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/complaints/${id}/messages`, { text });
      setText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendResponse = async () => {
    if (!resp.message) return toast.error("Add a message");
    try {
      await api.post(`/complaints/${id}/respond`, resp);
      toast.success("Response submitted"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const proposeSettlement = async () => {
    if (!sform.proposed_action) return toast.error("Describe the proposed action");
    try {
      await api.post(`/complaints/${id}/settlement`, {
        proposed_action: sform.proposed_action,
        refund_amount: parseFloat(sform.refund_amount) || null,
        completion_date: sform.completion_date || null, notes: sform.notes || null,
      });
      toast.success("Settlement proposed"); setSform({ proposed_action: "", refund_amount: "", completion_date: "", notes: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const respondSettlement = async (sid, decision) => {
    try { await api.post(`/settlements/${sid}/respond`, { decision }); toast.success(`Settlement ${decision}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const report = async () => {
    try { await api.post("/reports", { complaint_id: id, reason: "User reported content for moderation" }); toast.success("Reported for moderation review"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const [exporting, setExporting] = useState(false);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/complaints/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${c.complaint_code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Case PDF downloaded");
    } catch (e) { toast.error("Could not generate PDF"); }
    setExporting(false);
  };

  if (!c) return <div className="ca-container py-16 text-slate-500">Loading…</div>;

  return (
    <div className="ca-container py-10 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-sm text-slate-400">{c.complaint_code}</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">{c.party?.category || "Dispute"} — {c.party?.name}</h1>
          <p className="text-sm text-slate-500 mt-1">{c.party_type === "about_vendor" ? "Complaint about a vendor" : "Complaint about a customer"} · Filed {c.created_at?.slice(0, 10)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={c.status} />
          <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn"><Download className="h-4 w-4 mr-1" /> {exporting ? "Preparing…" : "Download PDF"}</Button>
          <Button variant="outline" size="sm" onClick={report} data-testid="report-btn"><Flag className="h-4 w-4 mr-1" /> Report</Button>
        </div>
      </div>

      {c.response_deadline && (c.status === "complaint_filed" || c.status === "response_pending") && (
        <div className="mt-3 rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
          Response deadline: {new Date(c.response_deadline).toLocaleString()} (7-day configurable period). No response does not automatically make the allegation true.
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="chat">
            <TabsList>
              <TabsTrigger value="chat" data-testid="tab-chat"><MessageSquare className="h-4 w-4 mr-1" /> Negotiation</TabsTrigger>
              <TabsTrigger value="settlement" data-testid="tab-settlement"><Handshake className="h-4 w-4 mr-1" /> Settlement</TabsTrigger>
              <TabsTrigger value="details" data-testid="tab-details"><FileText className="h-4 w-4 mr-1" /> Details</TabsTrigger>
            </TabsList>

            <TabsContent value="chat" className="mt-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[11px] text-slate-400 mb-2">Private room. Phone numbers stay masked. Only share contact details if you are comfortable; ComplaintAdvisor cannot control communication outside the platform.</div>
                <div className="h-72 overflow-y-auto space-y-2 pr-1">
                  {messages.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No messages yet. Start the discussion.</p>}
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === user.id ? "bg-teal-brand text-white" : "bg-slate-100 text-slate-800"}`}>
                        <p className="text-[10px] opacity-70">{m.sender_name} · {new Date(m.created_at).toLocaleTimeString()}</p>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div ref={chatEnd} />
                </div>
                <div className="mt-3 flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" data-testid="chat-input" onKeyDown={(e) => e.key === "Enter" && sendMsg()} />
                  <Button onClick={sendMsg} className="bg-teal-brand hover:bg-teal-700 text-white" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="settlement" className="mt-4 space-y-4">
              {settlements.map((s) => (
                <div key={s.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-slate-800">{s.proposed_action}</p>
                    <span className="text-xs font-semibold text-teal">{s.status}</span>
                  </div>
                  {s.refund_amount && <p className="text-sm text-slate-600 mt-1">Refund: ₹{s.refund_amount}</p>}
                  {s.completion_date && <p className="text-sm text-slate-600">By: {s.completion_date}</p>}
                  {s.notes && <p className="text-sm text-slate-500 mt-1">{s.notes}</p>}
                  {s.status !== "agreed" && s.proposer_id !== user.id && (
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => respondSettlement(s.id, "agree")} data-testid={`agree-settlement-${s.id}`}>Agree</Button>
                      <Button size="sm" variant="outline" onClick={() => respondSettlement(s.id, "counter")}>Counter</Button>
                      <Button size="sm" variant="outline" onClick={() => respondSettlement(s.id, "disagree")}>Disagree</Button>
                    </div>
                  )}
                  {s.status === "agreed" && <p className="mt-2 text-sm text-emerald-700 font-medium">Resolved by mutual agreement. This is a platform record, not a court order or legally binding agreement.</p>}
                </div>
              ))}
              {(isComplainant || isAccused) && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                  <p className="font-semibold text-slate-800">Propose a settlement</p>
                  <div><Label>Proposed action</Label><Input value={sform.proposed_action} onChange={(e) => setSform({ ...sform, proposed_action: e.target.value })} data-testid="settlement-action" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Refund amount (₹)</Label><Input type="number" value={sform.refund_amount} onChange={(e) => setSform({ ...sform, refund_amount: e.target.value })} data-testid="settlement-refund" /></div>
                    <div><Label>Completion date</Label><Input type="date" value={sform.completion_date} onChange={(e) => setSform({ ...sform, completion_date: e.target.value })} data-testid="settlement-date" /></div>
                  </div>
                  <div><Label>Notes</Label><Textarea rows={2} value={sform.notes} onChange={(e) => setSform({ ...sform, notes: e.target.value })} data-testid="settlement-notes" /></div>
                  <Button className="bg-teal-brand hover:bg-teal-700 text-white" onClick={proposeSettlement} data-testid="propose-settlement-btn"><Handshake className="h-4 w-4 mr-2" /> Propose settlement</Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="details" className="mt-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-2 text-sm">
                <p className="text-slate-700"><span className="text-slate-400">Description:</span> {c.description}</p>
                {c.desired_resolution && <p className="text-slate-700"><span className="text-slate-400">Desired resolution:</span> {c.desired_resolution}</p>}
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-400">Involved</p><p className="font-semibold">₹{c.amount_involved || 0}</p></div>
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-400">Paid</p><p className="font-semibold">₹{c.amount_paid || 0}</p></div>
                  <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-400">Pending</p><p className="font-semibold">₹{c.amount_pending || 0}</p></div>
                </div>
                <div className="pt-2">
                  <p className="text-xs text-slate-400 mb-1">Categories</p>
                  <div className="flex flex-wrap gap-1.5">{(c.categories || []).map((x) => <span key={x} className="rounded-full bg-teal-brand/10 text-teal px-2 py-0.5 text-xs">{x}</span>)}</div>
                </div>
                {c.evidence?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-xs text-slate-400 mb-2">Evidence (private · secure temporary links)</p>
                    <div className="flex flex-wrap gap-3">
                      {c.evidence.map((e, i) => <EvidenceThumb key={i} file={e} />)}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right rail */}
        <aside className="space-y-4">
          {isAccused && !c.response && (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" data-testid="respond-open-btn"><ShieldCheck className="h-4 w-4 mr-2" /> Respond / Challenge</Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle>Respond to this complaint</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Response type</Label>
                    <Select value={resp.response_type} onValueChange={(v) => setResp({ ...resp, response_type: v })}>
                      <SelectTrigger data-testid="response-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accept">Acknowledge receipt</SelectItem>
                        <SelectItem value="agree">Agree with complaint</SelectItem>
                        <SelectItem value="partial">Partially agree</SelectItem>
                        <SelectItem value="disagree">Disagree</SelectItem>
                        <SelectItem value="clarify">Request clarification</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Your explanation</Label><Textarea rows={4} value={resp.message} onChange={(e) => setResp({ ...resp, message: e.target.value })} data-testid="response-message" /></div>
                  <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" onClick={sendResponse} data-testid="submit-response-btn">Submit response</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {c.response && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold text-teal uppercase">Response received</p>
              <p className="text-sm text-slate-500 mt-1">Type: {c.response.response_type}</p>
              <p className="text-sm text-slate-700 mt-1">{c.response.message}</p>
            </div>
          )}
          {isComplainant && c.status !== "withdrawn" && c.status !== "resolved_by_agreement" && (
            <Button variant="outline" className="w-full" data-testid="withdraw-btn" onClick={async () => {
              try { await api.post(`/complaints/${id}/withdraw`); toast.success("Complaint withdrawn"); load(); } catch { toast.error("Failed"); }
            }}>Withdraw complaint</Button>
          )}
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-500">
            Complaints are allegations until verified or mutually resolved. Withdrawal does not independently establish whether the original allegation was true or false.
          </div>
        </aside>
      </div>
    </div>
  );
}
